import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  GraduationCap,
  PieChart,
  Building2,
  Users,
  Brain,
  LineChart,
  Briefcase,
  PlugZap,
  Handshake,
  ShoppingCart,
  Gamepad2,
  Search,
  LayoutGrid,
  Globe,
  Landmark,
  Megaphone,
} from "lucide-react";
import { collection, getDocs, query } from "firebase/firestore";
import { db } from "@/lib/firebase";

// === Progress helpers (localStorage) ===
function progressKey(catId: string, modId: string) {
  return `elearn:progress:${catId}:${modId}`;
}
function getProgress(catId: string, modId: string): { completed?: boolean; score?: number; ts?: number } {
  try { const raw = localStorage.getItem(progressKey(catId, modId)); return raw ? JSON.parse(raw) : {}; } catch { return {}; }
}
function setLastOpened(catId: string, modId: string) {
  try { localStorage.setItem("elearn:last_opened", JSON.stringify({ catId, modId, ts: Date.now() })); } catch {}
}
function getLastOpened() {
  try { const r = localStorage.getItem("elearn:last_opened"); return r ? JSON.parse(r) : null; } catch { return null; }
}
function countCompleted(catId: string, items: Subtopic[]) {
  try { return items.reduce((n, it) => n + (getProgress(catId, it.id)?.completed ? 1 : 0), 0); } catch { return 0; }
}
function totalSeedModules() {
  return CATEGORIES.reduce((acc, c) => acc + c.subtopics.length, 0);
}
function globalCompleted() {
  try { return CATEGORIES.reduce((acc, c) => acc + c.subtopics.filter((s) => getProgress(c.id, s.id)?.completed).length, 0); } catch { return 0; }
}

// --- Brand palette ---
const NAVY = "#1D314D"; // deep navy
const NAVY_2 = "#2E4663"; // secondary navy
const GOLD = "#CEAB65"; // metallic gold accent

// --- i18n helper ---
const text = {
  fr: {
    title: "E‑Learning",
    subtitle:
      "Parcourez les catégories, trouvez un module, et reprenez votre progression.",
    search: "Rechercher un module…",
    view_grid: "Grille",
    view_list: "Liste",
    results_for: (q: string) => `Résultats pour « ${q} »`,
    modules_count: (n: number) => `${n} module${n > 1 ? "s" : ""}`,
    none: "Aucun résultat.",
    start: "Commencer",
    more: "En savoir plus",
    ready: "Prêt",
    soon: "À venir",
    inProgress: "En chantier",
    resume_last: "Reprendre le dernier module",
    completed_label: "Complétés:",
  },
  en: {
    title: "E‑Learning",
    subtitle:
      "Browse categories, find a module, and pick up where you left off.",
    search: "Search a module…",
    view_grid: "Grid",
    view_list: "List",
    results_for: (q: string) => `Results for "${q}"`,
    modules_count: (n: number) => `${n} module${n > 1 ? "s" : ""}`,
    none: "No results.",
    start: "Start",
    more: "Learn more",
    ready: "Ready",
    soon: "Coming soon",
    inProgress: "In Progress",
    resume_last: "Resume last module",
    completed_label: "Completed:",
  },
};

// --- Types ---
export type Subtopic = {
  id: string;
  title: string; // resolved for current lang
  title_fr?: string;
  title_en?: string;
  status?: "ready" | "soon" | "in_progress";
  description?: string; // resolved for current lang
  description_fr?: string;
  description_en?: string;
  type?: "course" | "quiz" | "video";
  categoryId?: string;
  categoryTitle?: string; // resolved for current lang
};

export type Category = {
  id: string;
  title_fr: string;
  title_en: string;
  icon: JSX.Element;
  // seed subtopics used until Firestore provides data
  subtopics: (Omit<Subtopic, "title" | "description"> & { 
    id: string;
    title_fr: string; 
    title_en: string;
    description_fr?: string;
    description_en?: string;
  })[];
};

const CATEGORIES: Category[] = [
  {
    id: "economie",
    title_fr: "Économie",
    title_en: "Economics",
    icon: <PieChart className="h-5 w-5" />,
    subtopics: [
      {
        id: "micro",
        title_fr: "Microéconomie",
        title_en: "Microeconomics",
        status: "ready",
        type: "course",
        description_fr: "Fondamentaux: offre, demande, élasticité, coûts.",
        description_en: "Fundamentals: supply, demand, elasticity, costs.",
      },
      {
        id: "macro",
        title_fr: "Macroéconomie",
        title_en: "Macroeconomics",
        status: "ready",
        type: "course",
        description_fr: "PIB, inflation, chômage et cycles économiques.",
        description_en: "GDP, inflation, unemployment, business cycles.",
      },
    ],
  },
  {
    id: "finance",
    title_fr: "Finance",
    title_en: "Finance",
    icon: <BookOpen className="h-5 w-5" />,
    subtopics: [
      {
        id: "financement",
        title_fr: "Financement",
        title_en: "Financing",
        status: "ready",
        type: "course",
        description_fr: "Structure du capital, coûts, covenants.",
        description_en: "Capital structure, costs, covenants.",
      },
      {
        id: "prets",
        title_fr: "Prêts",
        title_en: "Loans",
        status: "ready",
        type: "quiz",
        description_fr: "Quiz: SBA vs. Term loan, DSCR, amortissement.",
        description_en: "Quiz: SBA vs term loan, DSCR, amortization.",
      },
    ],
  },
  {
    id: "financement-acquisition",
    title_fr: "Financement d'Acquisition",
    title_en: "Acquisition Financing",
    icon: <Landmark className="h-5 w-5" />,
    subtopics: [
      {
        id: "financement-bancaire",
        title_fr: "Financement bancaire traditionnel",
        title_en: "Traditional Bank Financing",
        status: "in_progress",
        type: "course",
        description_fr: "Prêt à terme, marge de crédit, ratios de couverture (DSCR), garanties exigées.",
        description_en: "Term loans, credit lines, debt coverage ratios (DSCR), required collateral.",
      },
      {
        id: "financement-gouvernemental",
        title_fr: "Financement gouvernemental — subventions, prêts avantageux et avantages fiscaux",
        title_en: "Government Financing — Grants, Favorable Loans & Tax Incentives",
        status: "in_progress",
        type: "course",
        description_fr: "SBA 7(a) et SBA 504 (US) · Programme de financement des petites entreprises du Canada (PFPEC) et prêts BDC · subventions régionales et crédits d'impôt à la reprise d'entreprise dans les deux pays.",
        description_en: "SBA 7(a) and SBA 504 (US) · Canada Small Business Financing Program (CSBFP) and BDC loans · regional grants and tax credits supporting business succession in both countries.",
      },
      {
        id: "financement-vendeur",
        title_fr: "Financement vendeur (Vendor Take-Back)",
        title_en: "Vendor Take-Back Financing",
        status: "in_progress",
        type: "course",
        description_fr: "Structure typique, avantages fiscaux pour le vendeur, négociation des termes et garanties.",
        description_en: "Typical structure, tax advantages for the seller, negotiating terms and security.",
      },
      {
        id: "capital-investissement",
        title_fr: "Capital-investissement et equity",
        title_en: "Private Equity & Equity Financing",
        status: "in_progress",
        type: "course",
        description_fr: "Private equity, family offices, business angels — quand et comment y avoir recours.",
        description_en: "Private equity, family offices, business angels — when and how to use them.",
      },
      {
        id: "financement-mezzanine",
        title_fr: "Financement mezzanine et dette subordonnée",
        title_en: "Mezzanine Financing & Subordinated Debt",
        status: "in_progress",
        type: "course",
        description_fr: "Positionnement dans la structure de capital, coût, covenants typiques.",
        description_en: "Position in the capital structure, cost, typical covenants.",
      },
      {
        id: "earnout-financement",
        title_fr: "Earnouts comme mécanisme de financement différé",
        title_en: "Earnouts as Deferred Financing",
        status: "in_progress",
        type: "course",
        description_fr: "Structurer un earnout, métriques de performance, pièges courants à éviter.",
        description_en: "Structuring an earnout, performance metrics, common pitfalls to avoid.",
      },
      {
        id: "financement-alternatif",
        title_fr: "Crowdfunding et financement alternatif",
        title_en: "Crowdfunding & Alternative Financing",
        status: "in_progress",
        type: "course",
        description_fr: "Options émergentes pour compléter un montage financier traditionnel.",
        description_en: "Emerging options to complement a traditional financing package.",
      },
      {
        id: "comparatif-us-canada-financement",
        title_fr: "Comparatif US vs Canada — disponibilité, coût, garanties",
        title_en: "US vs Canada Comparison — Availability, Cost, Collateral",
        status: "in_progress",
        type: "quiz",
        description_fr: "Quiz comparatif sur les différences de financement d'acquisition entre les deux marchés.",
        description_en: "Comparative quiz on acquisition financing differences between both markets.",
      },
    ],
  },
  {
    id: "technologie",
    title_fr: "Technologie",
    title_en: "Technology",
    icon: <PlugZap className="h-5 w-5" />,
    subtopics: [
      {
        id: "stack-smb",
        title_fr: "Stack SMB de base",
        title_en: "Core SMB stack",
        status: "ready",
        type: "video",
        description_fr: "Outils essentiels: CRM, facturation, paie, sauvegarde.",
        description_en: "Essentials: CRM, invoicing, payroll, backup.",
      },
      {
        id: "cybersecurite-dd",
        title_fr: "Cybersécurité en due diligence",
        title_en: "Cybersecurity in Due Diligence",
        status: "in_progress",
        type: "course",
        description_fr: "Risques et red flags à évaluer avant une acquisition.",
        description_en: "Risks and red flags to assess before an acquisition.",
      },
      {
        id: "transformation-digitale",
        title_fr: "Transformation digitale post-acquisition",
        title_en: "Post-Acquisition Digital Transformation",
        status: "in_progress",
        type: "course",
        description_fr: "Moderniser les processus d'une entreprise nouvellement acquise.",
        description_en: "Modernizing processes in a newly acquired business.",
      },
      {
        id: "selection-erp-crm",
        title_fr: "Sélection ERP/CRM lors d'une intégration",
        title_en: "ERP/CRM Selection During Integration",
        status: "in_progress",
        type: "course",
        description_fr: "Choisir et fusionner les systèmes après une acquisition.",
        description_en: "Choosing and merging systems after an acquisition.",
      },
      {
        id: "ia-automatisation-pme",
        title_fr: "Automatisation et IA dans les PME",
        title_en: "Automation & AI in SMBs",
        status: "in_progress",
        type: "course",
        description_fr: "Impact de la maturité technologique d'une entreprise sur sa valorisation.",
        description_en: "How a company's tech maturity impacts its valuation.",
      },
      {
        id: "propriete-intellectuelle-logicielle",
        title_fr: "Propriété intellectuelle logicielle et licences",
        title_en: "Software IP & Licensing",
        status: "in_progress",
        type: "course",
        description_fr: "Vérifier la propriété du code, licences tierces, dettes techniques.",
        description_en: "Verifying code ownership, third-party licenses, technical debt.",
      },
      {
        id: "data-rooms-outils-mna",
        title_fr: "Data rooms virtuelles et outils tech M&A",
        title_en: "Virtual Data Rooms & M&A Tech Tools",
        status: "in_progress",
        type: "course",
        description_fr: "Plateformes et outils spécialisés pour gérer une transaction.",
        description_en: "Specialized platforms and tools for managing a transaction.",
      },
      {
        id: "migration-cloud",
        title_fr: "Migration cloud vs on-premise",
        title_en: "Cloud vs On-Premise Migration",
        status: "in_progress",
        type: "quiz",
        description_fr: "Quiz sur les considérations techniques lors d'une intégration post-acquisition.",
        description_en: "Quiz on technical considerations during post-acquisition integration.",
      },
    ],
  },
  {
    id: "marketing-medias",
    title_fr: "Marketing, Médias & Réseaux Sociaux",
    title_en: "Marketing, Media & Social Media",
    icon: <Megaphone className="h-5 w-5" />,
    subtopics: [
      {
        id: "sale-readiness-image",
        title_fr: "Préparer une entreprise à la vente — image de marque",
        title_en: "Getting a Business Sale-Ready — Brand Image",
        status: "in_progress",
        type: "course",
        description_fr: "Comment l'image de marque et la présentation publique influencent la perception des acheteurs.",
        description_en: "How brand image and public presentation shape buyer perception.",
      },
      {
        id: "reputation-actif-intangible",
        title_fr: "Réputation en ligne comme actif intangible",
        title_en: "Online Reputation as an Intangible Asset",
        status: "in_progress",
        type: "course",
        description_fr: "Avis clients, notoriété digitale, impact mesurable sur la valorisation.",
        description_en: "Customer reviews, digital notoriety, measurable impact on valuation.",
      },
      {
        id: "due-diligence-digitale",
        title_fr: "Due diligence digitale",
        title_en: "Digital Due Diligence",
        status: "in_progress",
        type: "course",
        description_fr: "Ce que les acheteurs vérifient sur les réseaux sociaux et le web avant une offre.",
        description_en: "What buyers check on social media and the web before making an offer.",
      },
      {
        id: "confidentialite-visibilite",
        title_fr: "Confidentialité vs visibilité pendant une transaction",
        title_en: "Confidentiality vs Visibility During a Transaction",
        status: "in_progress",
        type: "course",
        description_fr: "Communiquer prudemment sans alerter employés, clients ou concurrents.",
        description_en: "Communicating carefully without alerting employees, clients, or competitors.",
      },
      {
        id: "contenu-dealmakers",
        title_fr: "Stratégie de contenu pour dealmakers",
        title_en: "Content Strategy for Dealmakers",
        status: "in_progress",
        type: "course",
        description_fr: "LinkedIn et networking digital pour générer des opportunités M&A.",
        description_en: "LinkedIn and digital networking to generate M&A opportunities.",
      },
      {
        id: "marketing-acquisition",
        title_fr: "Marketing d'acquisition — se positionner comme acheteur sérieux",
        title_en: "Acquisition Marketing — Positioning as a Serious Buyer",
        status: "in_progress",
        type: "course",
        description_fr: "Construire une présence qui attire les vendeurs et courtiers vers vous.",
        description_en: "Building a presence that attracts sellers and brokers to you.",
      },
      {
        id: "rp-post-cloture",
        title_fr: "Relations publiques post-clôture",
        title_en: "Post-Closing Public Relations",
        status: "in_progress",
        type: "course",
        description_fr: "Annoncer une acquisition ou fusion — ton, timing, parties prenantes à informer.",
        description_en: "Announcing an acquisition or merger — tone, timing, stakeholders to inform.",
      },
    ],
  },
  {
    id: "entreprise",
    title_fr: "Entreprise",
    title_en: "Business",
    icon: <Building2 className="h-5 w-5" />,
    subtopics: [
      { id: "actifs", title_fr: "Actifs (Assets)", title_en: "Assets", status: "ready", type: "course" },
      { id: "inventaire", title_fr: "Inventaire", title_en: "Inventory", status: "ready", type: "course" },
      { id: "culture", title_fr: "Culture d'entreprise", title_en: "Company culture", status: "ready", type: "video" },
      { id: "employes", title_fr: "Employés", title_en: "Employees", status: "ready", type: "course" },
      { id: "risques", title_fr: "Évaluation des risques", title_en: "Risk assessment", status: "ready", type: "quiz" },
      { id: "valeur", title_fr: "Évaluation de la valeur", title_en: "Valuation", status: "ready", type: "course" },
    ],
  },
  {
    id: "management",
    title_fr: "Management",
    title_en: "Management",
    icon: <Users className="h-5 w-5" />,
    subtopics: [
      { id: "leadership", title_fr: "Leadership", title_en: "Leadership", status: "ready", type: "course" },
      { id: "communication", title_fr: "Communication", title_en: "Communication", status: "ready", type: "course" },
      { id: "rh", title_fr: "Ressources humaines", title_en: "Human resources", status: "ready", type: "course" },
      { id: "rm", title_fr: "Ressources matérielles", title_en: "Physical resources", status: "ready", type: "course" },
      { id: "rt", title_fr: "Ressources technologiques", title_en: "Technological resources", status: "ready", type: "course" },
    ],
  },
  {
    id: "psychologie",
    title_fr: "Psychologie",
    title_en: "Psychology",
    icon: <Brain className="h-5 w-5" />,
    subtopics: [
      { id: "psy-vendeur", title_fr: "Psychologie pour vendeurs", title_en: "Psychology for sellers", status: "ready", type: "course" },
      { id: "psy-acheteur", title_fr: "Psychologie pour acheteurs", title_en: "Psychology for buyers", status: "ready", type: "course" },
      { id: "psy-general", title_fr: "Général", title_en: "General", status: "ready", type: "video" },
    ],
  },
  {
    id: "marche",
    title_fr: "Marché",
    title_en: "Market",
    icon: <LineChart className="h-5 w-5" />,
    subtopics: [
      { id: "analyse-marche", title_fr: "Analyse de marché", title_en: "Market analysis", status: "ready", type: "course" },
    ],
  },
  {
    id: "investissements",
    title_fr: "Investissements",
    title_en: "Investments",
    icon: <Briefcase className="h-5 w-5" />,
    subtopics: [
      { id: "intro-invest", title_fr: "Introduction", title_en: "Introduction", status: "ready", type: "course" },
    ],
  },
  {
    id: "integration",
    title_fr: "Intégration",
    title_en: "Onboarding",
    icon: <Handshake className="h-5 w-5" />,
    subtopics: [
      { id: "premiers-pas", title_fr: "Premiers pas", title_en: "Getting started", status: "ready", type: "video" },
    ],
  },
  {
    id: "vendre",
    title_fr: "Vendre",
    title_en: "Sell",
    icon: <ShoppingCart className="h-5 w-5" />,
    subtopics: [
      { id: "processus-vente", title_fr: "Processus de vente", title_en: "Sell process", status: "ready", type: "course" },
    ],
  },
  {
    id: "acheter",
    title_fr: "Acheter",
    title_en: "Buy",
    icon: <GraduationCap className="h-5 w-5" />,
    subtopics: [
      { id: "processus-achat", title_fr: "Processus d'achat", title_en: "Buy process", status: "ready", type: "course" },
    ],
  },
  {
    id: "game",
    title_fr: "Game",
    title_en: "Game",
    icon: <Gamepad2 className="h-5 w-5" />,
    subtopics: [
      {
        id: "quiz-intro",
        title_fr: "Quiz & Scénarios d'apprentissage",
        title_en: "Quizzes & Learning scenarios",
        status: "ready",
        type: "quiz",
        description_fr:
          "Mini‑jeux interactifs: QCM, mises en situation, et évaluations rapides.",
        description_en:
          "Interactive mini‑games: MCQs, scenarios, and quick assessments.",
      },
    ],
  },
];

// build search pool from both seeds and remote data
const buildSearchable = (categories: ReturnType<typeof useResolvedCategories>) =>
  categories.flatMap((c) => c.items.map((s) => ({ ...s, categoryId: c.id, categoryTitle: c.title })));

function useResolvedCategories(lang: "fr" | "en", remote: Record<string, Subtopic[]>) {
  return CATEGORIES.map((c) => {
    const title = lang === "fr" ? c.title_fr : c.title_en;
    // prefer Firestore modules if present
    const items: Subtopic[] = (remote[c.id] && remote[c.id].length > 0)
      ? remote[c.id]
      : c.subtopics.map((s) => ({
          ...s,
          title: lang === "fr" ? s.title_fr : s.title_en,
          description: lang === "fr" ? s.description_fr : s.description_en,
          categoryId: c.id,
          categoryTitle: title,
        }));
    return { id: c.id, title, icon: c.icon, items };
  });
}

export default function ELearningDashboard() {
  const [lang, setLang] = useState<"fr" | "en">("en");
  const t = text[lang];

  const [selected, setSelected] = useState<string>(CATEGORIES[0].id);
  const [queryStr, setQueryStr] = useState("");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [remoteModules, setRemoteModules] = useState<Record<string, Subtopic[]>>({});

  // fetch Firestore modules for a category (public read)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qref = collection(db, "elearning_categories", selected, "modules");
        const snap = await getDocs(query(qref));
        const mods: Subtopic[] = snap.docs.map((d) => {
          const data = d.data() as any;
          const title = lang === "fr" ? data.title_fr ?? data.title : data.title_en ?? data.title;
          const description = lang === "fr" ? data.description_fr ?? data.description : data.description_en ?? data.description;
          return {
            id: data.slug || d.id,
            title,
            title_fr: data.title_fr,
            title_en: data.title_en,
            description,
            description_fr: data.description_fr,
            description_en: data.description_en,
            status: (data.status as any) || "ready",
            type: data.type as any,
            categoryId: selected,
            categoryTitle: CATEGORIES.find((c) => c.id === selected) ? (lang === "fr" ? CATEGORIES.find((c) => c.id === selected)!.title_fr : CATEGORIES.find((c) => c.id === selected)!.title_en) : undefined,
          };
        });
        if (!cancelled) setRemoteModules((prev) => ({ ...prev, [selected]: mods }));
      } catch (e) {
        // silencieux: fallback seed data
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, lang]);

  const resolved = useResolvedCategories(lang, remoteModules);

  const current = resolved.find((c) => c.id === selected)!;
  const categoriesForSearch = buildSearchable(resolved);
  const items = useMemo(() => {
    const pool = queryStr.trim() ? categoriesForSearch : current.items;
    const filtered = queryStr.trim()
      ? pool.filter((s) =>
          (s.title + " " + (s.categoryTitle || "")).toLowerCase().includes(queryStr.toLowerCase())
        )
      : current.items;
    return filtered;
  }, [current, queryStr, categoriesForSearch]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1D314D] via-[#2E4663] to-[#0f172a] text-white" style={{ fontFamily: 'Geist, system-ui, sans-serif' }}>
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t.title}</h1>
            <p className="text-sm text-white/70">{t.subtitle}</p>
          </div>

          {/* Right controls: language, search, view */}
          <div className="flex flex-wrap items-center gap-2">
            <LanguageToggle lang={lang} setLang={setLang} />

            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-white/60" />
              <input
                value={queryStr}
                onChange={(e) => setQueryStr(e.target.value)}
                placeholder={t.search}
                className="w-64 rounded-xl border border-white/10 bg-white/10 px-8 py-2 text-sm placeholder:text-white/60 outline-none backdrop-blur transition focus:ring-2 focus:ring-[#CEAB65] focus:bg-white/15"
                aria-label={t.search}
              />
            </div>

            <button
              onClick={() => setView((v) => (v === "grid" ? "list" : "grid"))}
              className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm hover:bg-white/15 transition"
              aria-label="Toggle view"
            >
              <LayoutGrid className="h-4 w-4" /> {view === "grid" ? t.view_grid : t.view_list}
            </button>
          </div>
        </div>

        {/* Progress summary */}
        <ProgressSummary lang={lang} />

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
          {/* Sidebar */}
          <aside className="rounded-2xl border border-white/10 bg-white/5 p-3 backdrop-blur" aria-label="Categories">
            <nav className="flex flex-col gap-1">
              {resolved.map((cat) => {
                const active = selected === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setSelected(cat.id);
                      setQueryStr("");
                    }}
                    className={`group flex items-center justify-between rounded-xl px-3 py-2 text-left transition ${
                      active ? "bg-white/15 ring-1 ring-white/15" : "hover:bg-white/10"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="grid h-7 w-7 place-items-center rounded-lg" style={{ backgroundColor: "#1f2b3f" }}>
                        <span style={{ color: GOLD }}>{cat.icon}</span>
                      </span>
                      <span className="text-sm font-medium">{cat.title}</span>
                    </span>
                    <span className="rounded-md bg-white/10 px-2 py-0.5 text-xs text-white/80">{cat.items.length} · {countCompleted(cat.id, cat.items)}✓</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Main content */}
          <section className="min-h-[60vh]">
            <AnimatePresence mode="wait">
              <motion.div
                key={selected + view + (queryStr ? "_q" : "") + lang}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
              >
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-semibold tracking-tight">
                    {queryStr ? text[lang].results_for(queryStr) : current.title}
                  </h2>
                  <span className="text-xs text-white/70">{t.modules_count(items.length)}</span>
                </div>

                {items.length === 0 ? <p className="text-white/70">{t.none}</p> : null}

                {view === "grid" ? (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {items.map((s) => (
                      <ModuleCard key={s.id} sub={s} lang={lang} />
                    ))}
                  </div>
                ) : (
                  <ul className="divide-y divide-white/10 overflow-hidden rounded-2xl border border-white/10">
                    {items.map((s) => (
                      <li key={s.id}>
                        <ModuleRow sub={s} lang={lang} />
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            </AnimatePresence>
          </section>
        </div>
      </div>
    </div>
  );
}

function LanguageToggle({
  lang,
  setLang,
}: {
  lang: "fr" | "en";
  setLang: (l: "fr" | "en") => void;
}) {
  return (
    <div className="flex select-none items-center overflow-hidden rounded-xl border border-white/10 bg-white/10 text-xs">
      {(["fr", "en"] as const).map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={`flex items-center gap-1 px-2 py-1.5 transition ${
            lang === l ? "bg-white/15" : "hover:bg-white/5"
          }`}
        >
          <Globe className="h-3.5 w-3.5" /> {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function StatusBadge({ status = "ready", lang = "fr" as "fr" | "en" }) {
  const t = text[lang];
  const label = status === "soon" ? t.soon : status === "in_progress" ? t.inProgress : t.ready;
  const colorClasses =
    status === "soon"
      ? "bg-yellow-500/15 text-yellow-200"
      : status === "in_progress"
        ? "bg-orange-500/15 text-orange-200"
        : "bg-emerald-500/15 text-emerald-200";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${colorClasses}`}
    >
      {label}
    </span>
  );
}

function ModuleCard({ sub, lang }: { sub: Subtopic; lang: "fr" | "en" }) {
  const t = text[lang];
  const prog = getProgress(sub.categoryId || "cat", sub.id) || {};
  const done = !!prog.completed;
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur transition hover:-translate-y-0.5 hover:bg-white/10">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">{sub.title}{done && <span className="text-emerald-300 text-xs">✓</span>}</h3>
          {sub.description ? (
            <p className="mt-1 line-clamp-2 text-sm text-white/70">{sub.description}</p>
          ) : null}
        </div>
        <StatusBadge status={sub.status} lang={lang} />
      </div>
      <div className="mt-4 flex items-center gap-2">
        <a
          href={`#/e-learning/${sub.categoryId ?? "cat"}/${sub.id}`}
          className="rounded-xl px-3 py-2 text-xs font-semibold text-black transition hover:brightness-110"
          style={{ backgroundColor: GOLD }}
          onClick={() => setLastOpened(sub.categoryId || "cat", sub.id)}
        >
          {done ? (lang === 'fr' ? 'Revoir' : 'Resume') : t.start}
        </a>
        <button className="rounded-xl border border-white/10 px-3 py-2 text-xs hover:bg-white/10 transition">
          {t.more}
        </button>
      </div>
    </div>
  );
}

function ModuleRow({ sub, lang }: { sub: Subtopic; lang: "fr" | "en" }) {
  const t = text[lang];
  const prog = getProgress(sub.categoryId || "cat", sub.id) || {};
  const done = !!prog.completed;
  return (
    <div className="flex items-center justify-between gap-3 bg-white/5 px-4 py-3 hover:bg-white/10 transition">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-sm font-semibold flex items-center gap-2">{sub.title}{done && <span className="text-emerald-300">✓</span>}</h3>
          <StatusBadge status={sub.status} lang={lang} />
        </div>
        {sub.description ? <p className="truncate text-xs text-white/70">{sub.description}</p> : null}
      </div>
      <div className="flex items-center gap-2">
        <a
          href={`#/e-learning/${sub.categoryId ?? "cat"}/${sub.id}`}
          className="rounded-lg px-3 py-1.5 text-xs font-semibold text-black"
          style={{ backgroundColor: GOLD }}
          onClick={() => setLastOpened(sub.categoryId || "cat", sub.id)}
        >
          {done ? (lang === 'fr' ? 'Revoir' : 'Resume') : t.start}
        </a>
        <button className="rounded-lg border border-white/10 px-3 py-1.5 text-xs hover:bg-white/10 transition">
          {t.more}
        </button>
      </div>
    </div>
  );
}

function ProgressSummary({ lang }: { lang: "fr" | "en" }) {
  const t = text[lang];
  const total = totalSeedModules();
  const done = globalCompleted();
  const last = getLastOpened();
  const percentage = Math.min(100, Math.round((done / Math.max(1, total)) * 100));
  
  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1">
          <p className="text-sm text-white/80">
            {t.completed_label} {done} / {total}
          </p>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/10">
            <div 
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500" 
              style={{ width: `${percentage}%` }} 
            />
          </div>
        </div>
        {last && (
          <a 
            href={`#/e-learning/${last.catId}/${last.modId}`} 
            className="rounded-xl px-3 py-2 text-xs font-semibold text-black transition hover:brightness-110 whitespace-nowrap" 
            style={{ backgroundColor: GOLD }}
          >
            {t.resume_last}
          </a>
        )}
      </div>
    </div>
  );
}